/**
 * Cart Quantity-Variant Stepper + Unified Bulk Savings Display
 *
 * 1) For products where "Quantity" is a variant option, the Shopify cart qty
 *    is locked at 1 and +/- buttons step through variant tiers.
 *
 * 2) For ALL products (old + v2 templates), when the JS discount system
 *    populates [data-discount], reformat it to match the v2 "Bulk Savings"
 *    layout: heading + pill badge.
 *
 * FIXED 2026-08-21 (audit items 14 and 15):
 *  - Per-unit personalization is now reconciled to the new quantity. Stepping
 *    1 Sign to 3 Signs used to carry forward only "Sign points toward 1", so
 *    signs 2 and 3 reached production with no wording at all. Stepping down
 *    used to leave orphan properties for signs that no longer existed.
 *  - The page now reloads after a tier change instead of firing cart:build.
 *    The line item and total re-rendered on that event but the subtotal and
 *    the free-shipping bar did not, so the cart showed two different numbers
 *    until a manual refresh.
 */
(function () {

  // ── Per-unit property reconciliation ─────────────────────────────
  //
  // The personalizer writes one property per unit, named "<label> <n>":
  //   Sign points toward 1: Ceremony
  //   Sign points toward 2: Parking
  // Shared fields (Names, Wedding date, Notes) carry no number.
  //
  // When the tier changes, the per-unit set has to change with it.
  var PLACEHOLDER = 'To be confirmed';

  function reconcileProperties(props, newQty) {
    var perUnit = {};
    var passthrough = {};

    Object.keys(props).forEach(function (k) {
      // Internal properties (_Design Help, _Customer Upload 1, _Mockup Url)
      // are never per-unit. Some end in a digit, so they must be excluded
      // before the pattern match or we would invent _Customer Upload 2..N.
      if (k.charAt(0) === '_') { passthrough[k] = props[k]; return; }

      var m = k.match(/^(.+?)\s+(\d+)$/);
      if (!m) { passthrough[k] = props[k]; return; }

      var base = m[1];
      var n = parseInt(m[2], 10);
      if (!perUnit[base]) { perUnit[base] = {}; }
      perUnit[base][n] = props[k];
    });

    var out = {};
    Object.keys(passthrough).forEach(function (k) { out[k] = passthrough[k]; });

    Object.keys(perUnit).forEach(function (base) {
      // Guard: only treat this as a per-unit group if unit 1 exists. That way a
      // one-off shared field that happens to end in a number is left alone.
      if (perUnit[base][1] === undefined) {
        Object.keys(perUnit[base]).forEach(function (n) {
          out[base + ' ' + n] = perUnit[base][n];
        });
        return;
      }
      for (var i = 1; i <= newQty; i++) {
        var v = perUnit[base][i];
        out[base + ' ' + i] = (v === undefined || v === null || v === '') ? PLACEHOLDER : v;
      }
      // Units above newQty are intentionally dropped.
    });

    return out;
  }

  // ── Variant stepper ──────────────────────────────────────────────

  // Prevent theme's built-in qty component from initialising on variant wrappers
  var observer = new MutationObserver(function () {
    document.querySelectorAll('.js-qty__wrapper--variant .js-qty__num').forEach(function (input) {
      input.removeAttribute('name');
    });
    // Also reformat any newly-populated discount badges
    reformatDiscountBadges();
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });

  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('.js-qty__adjust');
    if (!btn) return;

    var wrapper = btn.closest('.js-qty__wrapper--variant');
    if (!wrapper) return;

    evt.preventDefault();
    evt.stopPropagation();

    var tiers = JSON.parse(wrapper.getAttribute('data-qty-tiers'));
    var currentVariantId = parseInt(wrapper.getAttribute('data-current-variant'), 10);
    var input = wrapper.querySelector('.js-qty__num');

    tiers.sort(function (a, b) { return a.qty - b.qty; });

    var currentIndex = 0;
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].id === currentVariantId) {
        currentIndex = i;
        break;
      }
    }

    var isMinus = btn.classList.contains('js-qty__adjust--minus');
    var nextIndex = isMinus ? currentIndex - 1 : currentIndex + 1;

    if (nextIndex < 0 || nextIndex >= tiers.length) return;

    var nextTier = tiers[nextIndex];
    if (nextTier.id === currentVariantId) return;

    input.value = nextTier.qty;
    wrapper.classList.add('is-loading');

    var properties = {};
    try {
      var propsRaw = JSON.parse(wrapper.getAttribute('data-properties'));
      if (Array.isArray(propsRaw)) {
        propsRaw.forEach(function (p) {
          if (p[0] && p[1] !== '') properties[p[0]] = p[1];
        });
      } else if (propsRaw && typeof propsRaw === 'object') {
        properties = propsRaw;
      }
    } catch (e) {}

    // Match the per-unit property set to the tier we are moving to.
    properties = reconcileProperties(properties, nextTier.qty);

    var lineKey = wrapper.getAttribute('data-line-key');

    fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      var addData = { id: nextTier.id, quantity: 1 };
      if (Object.keys(properties).length > 0) addData.properties = properties;
      return fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [addData] })
      });
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      // Full reload. A cart:build event only refreshed the line item and the
      // total, leaving the subtotal and the free-shipping bar showing stale
      // numbers. Those are server-rendered Liquid, so re-rendering them in JS
      // would mean duplicating the threshold logic and letting it drift again.
      window.location.reload();
    })
    .catch(function (err) {
      console.error('Variant swap failed:', err);
      input.value = tiers[currentIndex].qty;
      wrapper.classList.remove('is-loading');
    });

  }, true);


  // ── Reformat old-template discount badges ────────────────────────
  //
  // The theme's JS discount system populates small[data-discount] with
  // text like "5% Off (-$2.50)". We watch for that and restructure it
  // into the same layout the v2 template uses:
  //   BULK SAVINGS
  //   $x.xx/ea   Save X%
  //
  function reformatDiscountBadges() {
    document.querySelectorAll('small.cart__discount[data-discount]').forEach(function (el) {
      if (el.classList.contains('md-reformatted')) return;
      var text = el.textContent.trim();
      if (!text) return;

      el.classList.add('md-reformatted');

      // Build replacement markup
      var container = document.createElement('div');
      container.className = 'cart__bulk-savings-wrap';
      container.style.textAlign = 'right';
      container.style.marginTop = '6px';

      var heading = document.createElement('div');
      heading.className = 'cart__bulk-heading';
      heading.textContent = 'Bulk Savings';

      var badge = document.createElement('div');
      badge.className = 'cart__bulk-savings';
      badge.innerHTML = '<span class="cart__bulk-save-badge">' + text + '</span>';

      container.appendChild(heading);
      container.appendChild(badge);

      el.parentNode.insertBefore(container, el.nextSibling);
    });
  }

  // Run on load in case discounts are already rendered
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reformatDiscountBadges);
  } else {
    reformatDiscountBadges();
  }
  // Also run after cart rebuilds
  document.addEventListener('cart:build', function () {
    setTimeout(reformatDiscountBadges, 500);
  });

})();
